import { InputType } from '../../../../../types';
import {
  CreateCheckpointJSONDto,
  CreateOptionJSONDto,
  CreatePromptJSONDto,
  CreateQuestionJSONDto,
  CreateSurveyContentDto,
  CreateSurveyJSONDto,
  CreateSurveyJSONSchema,
} from './survey.dto';

/**
 * Severity of a survey config validation finding.
 * Errors block publish; warnings are informational and do not block draft save.
 */
export type SurveyConfigIssueLevel = 'error' | 'warning';

/**
 * Stable machine-readable code for a survey config validation finding.
 */
export type SurveyConfigIssueCode =
  | 'shape'
  | 'schema'
  | 'empty_questions'
  | 'missing_question_key'
  | 'duplicate_question_key'
  | 'bad_initial_question_key'
  | 'initial_question_mismatch'
  | 'bad_reminder_base_key'
  | 'branching_type_without_options'
  | 'bad_next_ref'
  | 'unreachable_question'
  | 'empty_question_text'
  | 'empty_option_text'
  | 'empty_prompt_text';

/**
 * A single survey configuration validation finding.
 */
export type SurveyConfigIssue = {
  level: SurveyConfigIssueLevel;
  code: SurveyConfigIssueCode;
  message: string;
};

/**
 * Result of validating survey create/publish JSON.
 */
export type ValidateSurveyConfigResult = {
  issues: SurveyConfigIssue[];
  /** Present when Zod schema parse succeeded. */
  parsed?: CreateSurveyJSONDto;
  /** True when no error-level issues (warnings alone still pass). */
  ok: boolean;
};

const OPTION_BRANCHING_TYPES: ReadonlySet<string> = new Set([
  InputType.RADIO,
  InputType.SELECT,
  InputType.MULTISELECT,
  InputType.CHECKBOX,
]);

/**
 * Recursively replaces JSON `null` with `undefined` so Zod `.optional()` fields accept exports that used null.
 *
 * @param value - Arbitrary JSON-shaped value
 * @returns Value with nulls stripped
 */
export function stripNulls<T>(value: T): T {
  if (value === null) {
    return undefined as T;
  }
  if (Array.isArray(value)) {
    return value.map(stripNulls) as T;
  }
  if (typeof value === 'object') {
    return Object.fromEntries(
      Object.entries(value).map(([k, v]) => [k, stripNulls(v)])
    ) as T;
  }
  return value;
}

/**
 * Normalizes either `{ key, surveyContent }` or a bare survey content object into CreateSurveyJSONDto.
 *
 * @param raw - Unknown JSON root
 * @returns CreateSurveyJSONDto shape
 * @throws Error when the root shape is unrecognized
 */
export function normalizeToCreateSurveyDto(raw: unknown): CreateSurveyJSONDto {
  if (raw === null || typeof raw !== 'object') {
    throw new Error('Root JSON value must be an object');
  }
  const o = raw as Record<string, unknown>;
  if (
    'surveyContent' in o &&
    o.surveyContent &&
    typeof o.surveyContent === 'object'
  ) {
    return raw as CreateSurveyJSONDto;
  }
  if (
    'surveyVersion' in o &&
    typeof o.key === 'string' &&
    typeof o.name === 'string'
  ) {
    return {
      key: o.key,
      surveyContent: raw as CreateSurveyContentDto,
    };
  }
  throw new Error(
    'Expected CreateSurveyJSONDto { key, surveyContent } or survey content with { key, name, surveyVersion }'
  );
}

/**
 * Sorts questions by sortOrder ascending.
 *
 * @param questions - Question list
 * @returns New sorted array
 */
function sortQuestions(
  questions: CreateQuestionJSONDto[]
): CreateQuestionJSONDto[] {
  return [...questions].sort((a, b) => {
    if (a.sortOrder !== b.sortOrder) {
      return a.sortOrder - b.sortOrder;
    }
    return 0;
  });
}

/**
 * Finds the next question by lowest sortOrder strictly greater than current.
 *
 * @param current - Current question
 * @param ordered - Questions sorted by sortOrder
 * @returns Next question or undefined if none
 */
function sortFallbackNext(
  current: CreateQuestionJSONDto,
  ordered: CreateQuestionJSONDto[]
): CreateQuestionJSONDto | undefined {
  const candidates = ordered.filter(q => q.sortOrder > current.sortOrder);
  if (candidates.length === 0) {
    return undefined;
  }
  const minOrder = Math.min(...candidates.map(q => q.sortOrder));
  return candidates.find(q => q.sortOrder === minOrder);
}

/**
 * Resolves the next question key after answering (option or question path).
 * Null/terminal means survey completes (default final checkpoint).
 * Cycles are allowed when keys resolve; only invalid refs are errors.
 *
 * @param q - Current question
 * @param option - Selected option, or undefined for single-path questions
 * @param keyToQuestion - Map of question key → question
 * @param ordered - Questions sorted by sortOrder
 * @returns Terminal, next key, or error
 */
export function nextKeyAfterAnswer(
  q: CreateQuestionJSONDto,
  option: CreateOptionJSONDto | undefined,
  keyToQuestion: Map<string, CreateQuestionJSONDto>,
  ordered: CreateQuestionJSONDto[]
):
  | { terminal: true }
  | { terminal: false; nextKey: string }
  | { error: string } {
  const checkpoint = option?.checkpoint ?? q.checkpoint;
  if (checkpoint?.isCompletionCheckpoint) {
    return { terminal: true };
  }

  const optNext = option?.nextQuestionKey?.trim();
  if (optNext) {
    if (!keyToQuestion.has(optNext)) {
      return {
        error: `nextQuestionKey "${optNext}" (from question "${q.key}"${
          option ? `, option sortOrder ${option.sortOrder}` : ''
        }) does not match any question key`,
      };
    }
    return { terminal: false, nextKey: optNext };
  }

  const qNext = q.nextQuestionKey?.trim();
  if (qNext) {
    if (!keyToQuestion.has(qNext)) {
      return {
        error: `nextQuestionKey "${qNext}" on question "${q.key}" does not match any question key`,
      };
    }
    return { terminal: false, nextKey: qNext };
  }

  const fb = sortFallbackNext(q, ordered);
  if (!fb || !fb.key?.trim()) {
    return { terminal: true };
  }
  return { terminal: false, nextKey: fb.key.trim() };
}

/**
 * Returns option branches for a question, or a single undefined branch when there are no options.
 *
 * @param q - Question
 * @returns Branches to evaluate for next-key resolution
 */
export function getBranchesForQuestion(
  q: CreateQuestionJSONDto
): (CreateOptionJSONDto | undefined)[] {
  const opts = q.options ?? [];
  if (opts.length > 0) {
    return opts.map(o => o);
  }
  return [undefined];
}

/**
 * Dedupes issues by level+code+message.
 *
 * @param items - Issue list
 * @returns Deduped list preserving first occurrence order
 */
export function dedupeIssues(items: SurveyConfigIssue[]): SurveyConfigIssue[] {
  const key = (i: SurveyConfigIssue) => `${i.level}:${i.code}:${i.message}`;
  const seen = new Set<string>();
  return items.filter(i => {
    const k = key(i);
    if (seen.has(k)) {
      return false;
    }
    seen.add(k);
    return true;
  });
}

/**
 * Validates survey flow graph: keys, next refs, checkpoints, reachability.
 * Cycles are allowed when references resolve.
 *
 * @param surveyContent - Parsed survey content
 * @param issues - Mutable issue collector
 */
export function validateSurveyFlow(
  surveyContent: CreateSurveyContentDto,
  issues: SurveyConfigIssue[]
): void {
  const version = surveyContent.surveyVersion;
  const questions = version?.questions ?? [];
  if (questions.length === 0) {
    issues.push({
      level: 'warning',
      code: 'empty_questions',
      message: 'surveyVersion.questions is empty; nothing to validate for flow',
    });
    return;
  }

  const missingKeys = questions.filter(q => !q.key?.trim());
  if (missingKeys.length > 0) {
    issues.push({
      level: 'error',
      code: 'missing_question_key',
      message: `${missingKeys.length} question(s) have empty or missing key`,
    });
    return;
  }

  const keyToQuestion = new Map<string, CreateQuestionJSONDto>();
  const seenKeys = new Map<string, number>();
  for (const q of questions) {
    const k = q.key!.trim();
    seenKeys.set(k, (seenKeys.get(k) ?? 0) + 1);
    keyToQuestion.set(k, q);
  }
  for (const [k, count] of seenKeys) {
    if (count > 1) {
      issues.push({
        level: 'error',
        code: 'duplicate_question_key',
        message: `Duplicate question key "${k}" appears ${count} times`,
      });
    }
  }

  const ordered = sortQuestions(questions);
  const startKey = ordered[0]?.key?.trim();
  if (!startKey) {
    return;
  }

  const initial = version?.initialQuestionKey?.trim();
  if (initial) {
    if (!keyToQuestion.has(initial)) {
      issues.push({
        level: 'error',
        code: 'bad_initial_question_key',
        message: `initialQuestionKey "${initial}" does not match any question key`,
      });
    } else if (initial !== startKey) {
      issues.push({
        level: 'warning',
        code: 'initial_question_mismatch',
        message: `initialQuestionKey "${initial}" differs from first question by sort order "${startKey}" (runtime uses first-by-sort-order as the entry question)`,
      });
    }
  }

  const visitCheckpointRefs = (
    cp: CreateCheckpointJSONDto | undefined,
    ctx: string
  ) => {
    const rk = cp?.reminderBaseDateQuestionKey?.trim();
    if (rk && !keyToQuestion.has(rk)) {
      issues.push({
        level: 'error',
        code: 'bad_reminder_base_key',
        message: `reminderBaseDateQuestionKey "${rk}" on ${ctx} does not match any question key`,
      });
    }
  };

  for (const q of questions) {
    visitCheckpointRefs(q.checkpoint, `question "${q.key}"`);
    for (const opt of q.options ?? []) {
      visitCheckpointRefs(
        opt.checkpoint,
        `question "${q.key}" option ${opt.sortOrder}`
      );
    }
  }

  for (const q of questions) {
    const t = q.type;
    if ((q.options ?? []).length === 0 && t && OPTION_BRANCHING_TYPES.has(t)) {
      issues.push({
        level: 'warning',
        code: 'branching_type_without_options',
        message: `Question "${q.key}" has type "${t}" but no options; flow is treated as a single linear step`,
      });
    }
  }

  for (const q of questions) {
    for (const opt of getBranchesForQuestion(q)) {
      const step = nextKeyAfterAnswer(q, opt, keyToQuestion, ordered);
      if ('error' in step) {
        issues.push({
          level: 'error',
          code: 'bad_next_ref',
          message: step.error,
        });
      }
    }
  }

  const reachable = new Set<string>();
  const walk = (qKey: string) => {
    if (reachable.has(qKey)) {
      return;
    }
    reachable.add(qKey);
    const q = keyToQuestion.get(qKey);
    if (!q) {
      return;
    }
    for (const opt of getBranchesForQuestion(q)) {
      const step = nextKeyAfterAnswer(q, opt, keyToQuestion, ordered);
      if ('error' in step) {
        continue;
      }
      if (!step.terminal && 'nextKey' in step) {
        walk(step.nextKey);
      }
    }
  };
  walk(startKey);

  for (const q of questions) {
    const k = q.key!.trim();
    if (!reachable.has(k)) {
      issues.push({
        level: 'warning',
        code: 'unreachable_question',
        message: `Question "${k}" is not reachable from the entry question "${startKey}" via nextQuestionKey / option next / sort-order fallback`,
      });
    }
  }
}

/**
 * Validates author-facing text completeness: question, option, and prompt copy.
 * Blank copy is allowed while drafting and only blocks publish.
 *
 * @param surveyContent - Parsed survey content
 * @param issues - Mutable issue collector
 */
export function validateSurveyContentText(
  surveyContent: CreateSurveyContentDto,
  issues: SurveyConfigIssue[]
): void {
  const questions = surveyContent.surveyVersion?.questions ?? [];

  const visitPrompt = (
    prompt: CreatePromptJSONDto | undefined,
    ctx: string
  ) => {
    if (!prompt) {
      return;
    }
    const blankFields = [
      !prompt.title?.trim() ? 'title' : null,
      !prompt.text?.trim() ? 'text' : null,
    ].filter((field): field is string => field !== null);
    if (blankFields.length > 0) {
      issues.push({
        level: 'error',
        code: 'empty_prompt_text',
        message: `Prompt on ${ctx} is missing ${blankFields.join(' and ')}`,
      });
    }
  };

  for (const q of questions) {
    const label = q.key?.trim() || `sortOrder ${q.sortOrder}`;

    if (!q.text?.trim()) {
      issues.push({
        level: 'error',
        code: 'empty_question_text',
        message: `Question "${label}" has no question text`,
      });
    }

    visitPrompt(q.prompt, `question "${label}"`);

    for (const opt of q.options ?? []) {
      if (!opt.text?.trim()) {
        issues.push({
          level: 'error',
          code: 'empty_option_text',
          message: `Question "${label}" option ${opt.sortOrder} has no option text`,
        });
      }
      visitPrompt(opt.prompt, `question "${label}" option ${opt.sortOrder}`);
    }
  }
}

/**
 * Validates survey create/publish JSON: Zod schema then content and flow/graph rules.
 * Accepts CreateSurveyJSONDto or bare survey content (`key` + `surveyVersion`).
 *
 * @param raw - Unknown survey JSON
 * @returns Issues, optional parsed DTO, and ok (no error-level issues)
 */
export function validateSurveyConfig(raw: unknown): ValidateSurveyConfigResult {
  const issues: SurveyConfigIssue[] = [];

  let dto: CreateSurveyJSONDto;
  try {
    dto = normalizeToCreateSurveyDto(raw);
  } catch (e) {
    const msg = e instanceof Error ? e.message : String(e);
    issues.push({ level: 'error', code: 'shape', message: msg });
    return { issues: dedupeIssues(issues), ok: false };
  }

  const parsed = CreateSurveyJSONSchema.safeParse(stripNulls(dto));
  if (!parsed.success) {
    for (const err of parsed.error.errors) {
      issues.push({
        level: 'error',
        code: 'schema',
        message: `${err.path.join('.') || '(root)'}: ${err.message}`,
      });
    }
    return { issues: dedupeIssues(issues), ok: false };
  }

  validateSurveyContentText(parsed.data.surveyContent, issues);
  validateSurveyFlow(parsed.data.surveyContent, issues);
  const finalIssues = dedupeIssues(issues);
  return {
    issues: finalIssues,
    parsed: parsed.data,
    ok: !finalIssues.some(i => i.level === 'error'),
  };
}
